import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { Backdrop, DialogContent, DialogTitle, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import StandardButton from 'components/buttons/StandardButton';
import InputWithActions from 'components/inputs/inputWithActions/InputWithActions';
import TMXPopover from 'components/popovers/TMXPopover';

import InputWithUnderline from './InputWithUnderline';
import useStyles from './style';
import { type ContentTypeEnum, defaultActiveInputValue, getInputHandlers, InputErrorEnum } from './utils';

type Props = {
  values: string[];
  onSubmit: (type: ContentTypeEnum, value: string[]) => void;
  inputRef: React.Ref<{ handleClick: (e: React.MouseEvent<HTMLElement>) => void }>;
  type: ContentTypeEnum;
};
export type ErrorState = {
  index: number;
  type: InputErrorEnum;
};

const EditableContactDetails = ({ values, onSubmit, inputRef, type }: Props) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const invisibleInputRef = useRef(null);
  const [error, setError] = useState<ErrorState>({ index: undefined, type: undefined });
  const [anchorEl, setAnchorEl] = useState(null);
  const [confirmationDialog, setConfirmationDialog] = useState(false);
  const { isValidInput, formatDisplayValue, formatInputValue, parseInputValue, getInputTitle, getErrorMessage } =
    useMemo(() => getInputHandlers(type), [type]);

  const [activeInput, setActiveInput] = useState(defaultActiveInputValue);

  const handleAddNew = useCallback(() => {
    setAnchorEl(invisibleInputRef.current);
    setActiveInput({
      value: undefined,
      index: values.length,
    });
  }, [values.length]);

  useImperativeHandle(inputRef, () => ({
    handleClick: () => handleAddNew(),
  }));

  const closeDialog = useCallback(() => {
    setAnchorEl(null);
  }, []);
  const openDialog = useCallback(
    (e: React.MouseEvent<HTMLElement>, val: string, index: number) => {
      setAnchorEl(e.currentTarget);
      setActiveInput({
        value: formatInputValue(val),
        index: index,
      });
    },
    [formatInputValue],
  );
  useEffect(() => {
    if (anchorEl === null && error !== null) {
      setError(null);
    }
  }, [anchorEl, error, activeInput.index, setActiveInput]);

  const toggleConfirmationDialog = useCallback(() => {
    setConfirmationDialog((prev) => !prev);
  }, []);

  const handleChange = useCallback(
    (val: string) => {
      setActiveInput({
        value: formatInputValue(val),
        index: activeInput.index,
      });
    },
    [activeInput.index, formatInputValue],
  );
  const handleDelete = useCallback(() => {
    closeDialog();
    toggleConfirmationDialog();
    onSubmit(
      type,
      values.filter((_, index) => activeInput.index !== index),
    );
  }, [activeInput.index, closeDialog, onSubmit, toggleConfirmationDialog, type, values]);

  const handleReset = useCallback(() => {
    handleChange(values[activeInput.index]);
  }, [activeInput.index, handleChange, values]);

  const handleSubmit = useCallback(() => {
    try {
      const parsedInputValue = parseInputValue(activeInput.value);
      if (!isValidInput(activeInput.value)) {
        throw new Error(InputErrorEnum.VALIDATION);
      }
      if (values.includes(parsedInputValue) && values[activeInput.index] !== parsedInputValue) {
        throw new Error(InputErrorEnum.DUPLICATE);
      }
    } catch (err: any) {
      return setError({ index: activeInput.index, type: err.message });
    }
    closeDialog();
    onSubmit(
      type,
      values[activeInput.index]
        ? values.map((value, index) => (index === activeInput.index ? activeInput.value : value))
        : [...values, activeInput.value],
    );
  }, [activeInput, closeDialog, isValidInput, onSubmit, parseInputValue, type, values]);

  const errorMessage = getErrorMessage(t, error, activeInput, type);

  return (
    <>
      <div className={classes.inputWrapper}>
        {values?.map((value: string, index) => (
          <InputWithUnderline
            key={value}
            index={index}
            label={getInputTitle(t, type, index)}
            value={formatDisplayValue(value)}
            onClick={(e) => openDialog(e, value, index)}
          />
        ))}
      </div>

      <div ref={invisibleInputRef} className={classes.invisibleInput} />
      <TMXPopover
        anchorEl={anchorEl}
        open={!!anchorEl}
        sx={{ '& .MuiPaper-root': { maxWidth: '500px' } }}
        onClose={closeDialog}
      >
        {!confirmationDialog ? (
          <>
            <DialogTitle className={classes.dialogTitle}>{getInputTitle(t, type, activeInput.index)}</DialogTitle>
            <DialogContent className={classes.dialogContent}>
              <InputWithActions
                deleteOption={activeInput.index !== 0 && !!values[activeInput.index]}
                error={errorMessage}
                handleChange={handleChange}
                handleDelete={toggleConfirmationDialog}
                handleReset={handleReset}
                resetOption={
                  activeInput.value !== formatInputValue(values[activeInput.index]) &&
                  activeInput.index === 0 &&
                  !!values[activeInput.index]
                }
                value={activeInput.value}
              />
              <Grid container gap={1.5}>
                <StandardButton id="save" variant="primary" onClick={handleSubmit}>
                  {t('save')}
                </StandardButton>
                <StandardButton id="cancel" onClick={closeDialog}>
                  {t('ccl')}
                </StandardButton>
              </Grid>
            </DialogContent>
          </>
        ) : (
          <>
            <DialogTitle className={cx(classes.dialogTitle, classes.deleteDialogTitle)}>
              {t('delete')} {type}
            </DialogTitle>
            <DialogContent className={classes.deleteDialogContent}>
              <Grid container gap={2}>
                <Grid container direction={'column'} gap={1.5}>
                  <Typography>
                    {formatDisplayValue(values[activeInput?.index])} {t('deleteText')}
                  </Typography>
                  <Typography>{t('confirmationText')}</Typography>
                </Grid>
                <Grid container gap={1.5} justifyContent={'flex-end'} style={{ marginTop: '10px' }}>
                  <StandardButton id="cancel" onClick={toggleConfirmationDialog}>
                    {t('ccl')}
                  </StandardButton>
                  <StandardButton id="confirm" variant="warning" onClick={handleDelete}>
                    {t('delete')}
                  </StandardButton>
                </Grid>
              </Grid>
            </DialogContent>
          </>
        )}
      </TMXPopover>
      <Backdrop className={classes.backdrop} open={!!anchorEl} onClick={closeDialog} />
    </>
  );
};

export default EditableContactDetails;
